import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import * as api from '../api';
import Register from '../../hr/components/Register';
import { can, usePyMeta } from '../py';
import { Approval } from '../shared';
import { payrollSubUrl } from '../catalog';

// Stephanet's Compensation group (Salary Changes, Salary Grades, Benefits Admin, Allowances, Bonuses & Performance Pay, Benefit Policy
// Engine, Compensation Budgeting) and Pay Structure group (Payment Structure, Payroll Runs Register, Payroll Register): its register
// views (EntityView), here the HR register. Kept from this app (the user's decisions): salary changes and pay items are approved on the
// approval engine (PAY_SALARY_CHANGE, PAY_ITEM) — the detail shows the route, and the approver's buttons come from the engine.

const STATUS = ['Draft', 'Submitted', 'Returned', 'Approved', 'Rejected', 'Cancelled'];
const NOTE = [{ name: 'note', label: 'Comment', type: 'textarea', required: true }];
const ACTION = {
  submit: { label: 'Submit for approval', tone: 'primary', done: 'Submitted for approval' },
  approve: { label: 'Approve', tone: 'primary', done: 'Approved' },
  return: { label: 'Return for correction', fields: NOTE, done: 'Returned for correction' },
  reject: { label: 'Reject', tone: 'danger', fields: NOTE, done: 'Rejected' },
  cancel: { label: 'Cancel request', tone: 'danger', confirm: 'Cancel this request?', done: 'Cancelled' },
  reopen: { label: 'Reopen', confirm: 'Reopen as a draft? It is not used until approved again.', done: 'Reopened' },
};

function ApprovalBox({ get }) {
  const [a, setA] = useState(null);
  useEffect(() => { get().then(setA).catch(() => setA({ route: [], history: [] })); }, [get]);
  if (!a) return <div className="muted small">Loading the approval…</div>;
  if (!a.route.length) return null;
  return <div style={{ marginTop: 12 }}><h4 className="wh-sub">Approval</h4><Approval route={a.route} history={a.history} actions={[]} onAction={() => {}} /></div>;
}

// the engine's actions on a record (r.actions from the server) as register buttons
const engineActions = (r, act, extra = {}) => (r.actions || []).filter(a => ACTION[a] || extra[a]).map(a => {
  const x = extra[a] || ACTION[a];
  return { ...x, run: (rec, payload) => act(rec.id, { action: a, ...(payload || {}) }) };
});

function Managed({ note, to, title }) {
  return <div className="alert info" style={{ marginBottom: 12 }}>{note} <Link to={payrollSubUrl(to)}>Open {title} →</Link></div>;
}

// ------------------------------------------------------------------ SALARY CHANGES

export function SalaryChanges() {
  const [meta] = usePyMeta();
  const [opts, setOpts] = useState({ employees: [], grades: [], types: [] });
  const load = useCallback(() => api.salaryChanges().then(r => { setOpts(r); return r.rows; }), []);
  const res = useMemo(() => ({
    key: 'salary_changes', label: 'Salary Change', plural: 'Salary Changes', title: r => `${r.reference} · ${r.employee}`, load,
    statusField: 'status',
    create: d => api.saveSalaryChange(null, d), update: (r, d) => api.saveSalaryChange(r.id, d),
    canUpdate: r => r.actions.includes('edit'), remove: r => api.salaryChangeAction(r.id, { action: 'delete' }), canRemove: r => r.actions.includes('delete'),
    actions: r => engineActions(r, api.salaryChangeAction),
    renderDetail: r => <ApprovalBox get={() => api.salaryChangeApproval(r.id)} />,
    search: ['reference', 'employee_no', 'employee', 'change_type', 'reason'],
    fields: [
      { name: 'reference', label: 'Reference', list: true, form: false },
      { name: 'employee', label: 'Employee', list: true, form: false, get: r => `${r.employee_no} — ${r.employee}` },
      { name: 'employee_id', label: 'Employee', type: 'select', required: true, options: opts.employees, detail: false },
      { name: 'effective_date', label: 'Effective Date', type: 'date', required: true, list: true },
      { name: 'change_type', label: 'Change Type', type: 'select', required: true, options: opts.types, list: true },
      { name: 'old_grade', label: 'Old Grade', form: false },
      { name: 'old_salary', label: 'Old Monthly Pay', type: 'money', list: true, form: false },
      { name: 'new_grade_id', label: 'New Grade', type: 'select', options: opts.grades, detail: false, placeholder: 'Blank = no grade change' },
      { name: 'new_grade', label: 'New Grade', list: true, form: false },
      { name: 'new_pay', label: 'New Monthly Pay', type: 'money', detail: false, placeholder: 'Blank = the grade amount' },
      { name: 'new_salary', label: 'New Monthly Pay', type: 'money', list: true, form: false },
      { name: 'change_pct', label: 'Change', form: false, list: true, get: r => (r.change_pct === null ? '—' : `${(r.change_pct * 100).toFixed(1)}%`) },
      { name: 'reason', label: 'Reason', type: 'textarea', required: true },
      { name: 'status', label: 'Status', pill: true, list: true, form: false, options: STATUS },
      { name: 'applied', label: 'On the employee record', type: 'boolean', form: false },
    ],
  }), [load, opts]);
  return (
    <>
      <div className="alert info" style={{ marginBottom: 12 }}>
        Approval writes the new grade and pay to the employee's record from the effective date's payroll month — a future-dated change takes effect
        from that month. A backdated change does not pay arrears; enter them as Additions on Monthly Deductions.
      </div>
      <Register res={res} canEdit={can(meta, 'salary-changes')} />
    </>
  );
}

// ------------------------------------------------------------------ BENEFITS ADMIN / ALLOWANCES / BONUSES / DEDUCTION MANAGEMENT

const ITEM_TEXT = {
  benefits: ['Employee Benefit', 'Employee Benefits', 'Benefits paid to one employee on top of the grade benefits in Benefits Setup.'],
  allowances: ['Allowance', 'Allowances', 'Allowances paid to one employee each month (or once).'],
  bonuses: ['Bonus', 'Bonuses', 'Bonuses and performance pay — skipped for staff whose HR record says they are not bonus-eligible.'],
  deductions: ['Deduction', 'Deductions', 'Union, cooperative and other standing deductions taken from net pay.'],
};

export function PayItems({ kind }) {
  const [meta] = usePyMeta();
  const [opts, setOpts] = useState({ employees: [], components: [], sources: [] });
  const load = useCallback(() => api.payItems(kind).then(r => { setOpts(r); return r.rows; }), [kind]);
  const [label, plural, text] = ITEM_TEXT[kind];
  const res = useMemo(() => ({
    key: `pay_${kind}`, label, plural, title: r => `${r.reference} · ${r.employee}`, load, statusField: 'status',
    create: d => api.savePayItem(kind, null, d), update: (r, d) => api.savePayItem(kind, r.id, d),
    canUpdate: r => r.actions.includes('edit'), remove: r => api.payItemAction(r.id, { action: 'delete' }), canRemove: r => r.actions.includes('delete'),
    actions: r => engineActions(r, api.payItemAction, { stop: { label: 'Stop after…', fields: [{ name: 'end_period', label: 'Last month', type: 'month', required: true }], done: 'End month set' } }),
    renderDetail: r => <ApprovalBox get={() => api.payItemApproval(r.id)} />,
    defaults: { recurring: true, source: 'Manual' },
    search: ['reference', 'employee_no', 'employee', 'component', 'description'],
    fields: [
      { name: 'reference', label: 'Reference', list: true, form: false },
      { name: 'employee', label: 'Employee', list: true, form: false, get: r => `${r.employee_no} — ${r.employee}` },
      { name: 'employee_id', label: 'Employee', type: 'select', required: true, options: opts.employees, detail: false },
      { name: 'component_id', label: 'Component', type: 'select', required: true, options: opts.components, detail: false },
      { name: 'component', label: 'Component', list: true, form: false },
      { name: 'description', label: 'Description', placeholder: 'Blank = the component name', list: true },
      { name: 'amount', label: 'Monthly Amount', type: 'money', required: true, list: true },
      { name: 'taxable', label: 'Taxable', type: 'boolean', form: false },
      { name: 'source', label: 'Source', type: 'select', options: opts.sources },
      { name: 'recurring', label: 'Recurring', type: 'boolean', list: true },
      { name: 'start_period', label: 'Start Period', type: 'month', required: true, list: true },
      { name: 'end_period', label: 'End Period (blank = open-ended)', type: 'month' },
      { name: 'status', label: 'Status', pill: true, list: true, form: false, options: STATUS },
      { name: 'note', label: 'Note', type: 'textarea' },
    ],
  }), [kind, label, plural, load, opts]);
  return (
    <>
      <div className="alert info" style={{ marginBottom: 12 }}>
        {text} Payroll uses approved items whose months cover the payroll month. The types come from Pay Structure › Payment Structure.
      </div>
      <Register res={res} canEdit={can(meta, kind)} />
    </>
  );
}
export const BenefitsAdmin = () => <PayItems kind="benefits" />;
export const Allowances = () => <PayItems kind="allowances" />;
export const Bonuses = () => <PayItems kind="bonuses" />;

// ------------------------------------------------------------------ SALARY GRADES (same records as Setup › Salary Grades)

export function GradesView() {
  const load = useCallback(() => api.gradesSheet().then(r => r.grades), []);
  const res = useMemo(() => ({
    key: 'pay_grades', label: 'Salary Grade', plural: 'Salary Grades', title: r => r.code, load,
    search: ['code', 'name', 'group', 'ho_title', 'tech_title'],
    fields: [
      { name: 'code', label: 'Grade', list: true }, { name: 'name', label: 'Name', list: true }, { name: 'group', label: 'Group', list: true },
      { name: 'ho_title', label: 'Head Office Title', list: true }, { name: 'tech_title', label: 'Technical Title', list: true },
      { name: 'monthly', label: 'Monthly', type: 'money', list: true }, { name: 'daily', label: 'Daily', type: 'money', list: true },
      { name: 'profile', label: 'Absence Profile', list: true },
    ],
  }), [load]);
  return (
    <>
      <Managed note="Same grades as Setup › Salary Grades. Basic, housing, transport and the other allowances are split from the grade amount by the Setup salary components."
               to="py-grades" title="Salary Grades" />
      <Register res={res} canEdit={false} />
    </>
  );
}

// ------------------------------------------------------------------ BENEFIT POLICY ENGINE (Benefits Setup, locked)

export function BenefitPolicies() {
  const load = useCallback(() => api.benefitPolicies().then(r => r.rows), []);
  const res = useMemo(() => ({
    key: 'benefit_policies', label: 'Benefit Policy', plural: 'Benefit Policies', title: r => r.name, load,
    fields: [
      { name: 'name', label: 'Policy', list: true }, { name: 'eligibility', label: 'Eligibility (grades / types)', list: true },
      { name: 'benefit', label: 'Benefit', list: true }, { name: 'amount', label: 'Amount', type: 'money', list: true },
      { name: 'frequency', label: 'Frequency', list: true }, { name: 'active', label: 'Active', type: 'boolean', list: true },
      { name: 'setup_ref', label: 'Kept in step with Payroll Setup', list: true },
    ],
  }), [load]);
  return (
    <>
      <Managed note="Every benefit in Benefits Setup is listed here with who is entitled and how often, and locked." to="py-benefits-setup" title="Benefits Setup" />
      <Register res={res} canEdit={false} />
    </>
  );
}

// ------------------------------------------------------------------ COMPENSATION BUDGETING

export function CompBudgets() {
  const [meta] = usePyMeta();
  const [depts, setDepts] = useState([]);
  const load = useCallback(() => api.compBudgets().then(r => { setDepts(r.departments); return r.rows.map(x => ({ ...x, increase_pct: +(x.increase_pct * 100).toFixed(2) })); }), []);
  const body = d => ({ ...d, increase_pct: d.increase_pct === '' || d.increase_pct === undefined || d.increase_pct === null ? 0 : Number(d.increase_pct) / 100 });
  const res = useMemo(() => ({
    key: 'compensation_budgets', label: 'Compensation Budget', plural: 'Compensation Budgets', title: r => `${r.year} · ${r.department}`, load,
    create: d => api.saveCompBudget(null, body(d)), update: (r, d) => api.saveCompBudget(r.id, body(d)), remove: r => api.deleteCompBudget(r.id),
    defaults: { year: new Date().getFullYear() },
    search: ['year', 'department'],
    fields: [
      { name: 'year', label: 'Year', type: 'integer', required: true, list: true },
      { name: 'department', label: 'Department', list: true, form: false },
      { name: 'department_id', label: 'Department', type: 'select', required: true, options: depts, detail: false },
      { name: 'headcount', label: 'Planned Headcount', type: 'integer', list: true },
      { name: 'salary_budget', label: 'Salary Budget', type: 'money', list: true },
      { name: 'increase_pct', label: 'Planned Increase %', type: 'number', list: true },
      { name: 'bonus_pool', label: 'Bonus Pool', type: 'money', list: true },
      { name: 'actual_salary', label: 'Paid so far (approved runs)', type: 'money', list: true, form: false },
      { name: 'remaining', label: 'Budget left', type: 'money', list: true, form: false },
      { name: 'actual_staff', label: 'Staff paid', type: 'integer', form: false },
      { name: 'actual_bonus', label: 'Bonuses paid', type: 'money', form: false },
      { name: 'note', label: 'Note', type: 'textarea' },
    ],
  }), [load, depts]);
  return <Register res={res} canEdit={can(meta, 'comp-budget')} />;
}

// ------------------------------------------------------------------ PAY STRUCTURE: PAYMENT STRUCTURE, PAYROLL RUNS REGISTER

export function PaymentStructure() {
  const [meta] = usePyMeta();
  const [opts, setOpts] = useState({ types: [], methods: [] });
  const load = useCallback(() => api.payStructure().then(r => { setOpts(r); return [...r.locked, ...r.components.map(c => ({ ...c, managed_by: '' }))]; }), []);
  const res = useMemo(() => ({
    key: 'pay_components', label: 'Pay Component', plural: 'Pay Components', title: r => r.name, load,
    create: d => api.saveComponent(null, d), update: (r, d) => api.saveComponent(r.id, d), remove: r => api.deleteComponent(r.id),
    canUpdate: r => !r.managed_by, canRemove: r => !r.managed_by && !r.used,
    defaults: { component_type: 'Allowance', calc_method: 'Fixed', default_value: 0, taxable: true, is_active: true },
    search: ['name', 'component_type', 'managed_by'],
    fields: [
      { name: 'name', label: 'Component', required: true, list: true },
      { name: 'component_type', label: 'Type', type: 'select', required: true, options: opts.types, list: true },
      { name: 'calc_method', label: 'Calculation', type: 'select', options: opts.methods, list: true },
      { name: 'default_value', label: 'Amount / Rate', type: 'number', list: true },
      { name: 'taxable', label: 'Taxable', type: 'boolean', list: true },
      { name: 'is_active', label: 'Active', type: 'boolean', list: true },
      { name: 'managed_by', label: 'Kept in step with Payroll Setup', list: true, form: false, get: r => r.managed_by || '—' },
      { name: 'used', label: 'Employee pay items', type: 'integer', form: false },
    ],
  }), [load, opts]);
  return (
    <>
      <div className="alert info" style={{ marginBottom: 12 }}>
        Salary components (Setup Rules) and benefits (Benefits Setup) are listed here automatically and locked. Add allowance, benefit, bonus and
        deduction types here — they are what Compensation's employee pay items are made of.
      </div>
      <Register res={res} canEdit={can(meta, 'payment-structure')} />
    </>
  );
}

export function RunsList() {
  const load = useCallback(() => api.runs().then(r => r.runs), []);
  const res = useMemo(() => ({
    key: 'payroll_runs', label: 'Payroll Run', plural: 'Payroll Runs', title: r => r.code, load, statusField: 'status',
    detailLink: () => ({ to: payrollSubUrl('payroll-engine'), label: 'Open Payroll Runs & Approval' }),
    search: ['code', 'period', 'status'],
    fields: [
      { name: 'code', label: 'Run', list: true }, { name: 'period', label: 'Period', list: true },
      { name: 'status', label: 'Status', pill: true, list: true, options: ['Draft', 'Calculated', 'Submitted', 'Returned', 'Approved', 'Paid'] },
      { name: 'employees', label: 'Employees', type: 'integer', list: true }, { name: 'gross', label: 'Total Gross', type: 'money', list: true },
      { name: 'deductions', label: 'Total Deductions', type: 'money', list: true }, { name: 'net', label: 'Total Net', type: 'money', list: true },
      { name: 'employer', label: 'Employer Cost', type: 'money', list: true }, { name: 'notes', label: 'Notes' },
    ],
  }), [load]);
  return <Register res={res} canEdit={false} />;
}
